// Database contract — every table, view, RPC, storage bucket and status
// vocabulary the app touches, in ONE place. Extracted from the legacy snapshot
// (legacy/v20_Index.html) by auditing every `_sb.from(`, `_sb.rpc(` and
// `storage.from(` call site. Nothing may reference a table/RPC/bucket name as
// a bare string literal outside this module.

// ─── Tables (PostgREST-exposed base tables) ────────────────────────────────
export const TABLES = Object.freeze({
  ADVERSE_EVENTS: 'adverse_events',
  APP_LOGINS: 'app_logins',
  APP_USERS: 'app_users',
  AUDIT_EVENTS: 'audit_events',
  CASE_STAGE_HISTORY: 'case_stage_history',
  CASES: 'cases',
  CONSENT_SUBMISSION: 'consent_submission',
  CONSENTS: 'consents',
  CONTACTS: 'contacts',
  DELIVERIES: 'deliveries',
  DOCUMENTS: 'documents',
  ESCALATIONS: 'escalations',
  EXPENSES: 'expenses',
  FACILITIES: 'facilities',
  LEGACY_EPISODES: 'legacy_episodes',
  MEDICATION_CYCLES: 'medication_cycles',
  PROGRAMMES: 'programmes',
  TASKS: 'tasks',
  TRAINING_VIDEOS: 'training_videos',
  TRAINING_EVENTS: 'training_events',
  TRAINING_QUESTIONS: 'training_questions',
  TRAINING_ANSWERS: 'training_answers',
});

// ─── Views (read-only) ─────────────────────────────────────────────────────
export const VIEWS = Object.freeze({
  CT_QUEUE_ALL: 'v_ct_queue_all',
  PROGRAMME_DASHBOARD: 'v_programme_dashboard',
  SAFETY_RECONCILIATION: 'v_safety_reconciliation',
});

// ─── v_ct_queue_all queue_type naming convention (review R-17) ─────────────
// THE RULE (what isSafetyQueueItem actually checks): a queue row is a SAFETY
// item — shown to every nurse regardless of the "My patients" scope — when
// its queue_type, split on any non-alphanumeric character, contains one of
// the WHOLE TOKENS below: `ae`, `safety`, `serious`. Whole tokens, not
// substrings: a type like `maelezo_review` or `aerosol_refill` must not
// bypass scope by accident. Any view change that adds a safety queue_type
// must split into one of these three tokens.
//
// KNOWN VALUES (not the tokens — item 14, second-opinion pass: an earlier
// version of this comment listed these as if they were "the WHOLE tokens",
// which cannot ever be true, since splitting always breaks a multi-word
// queue_type apart before the `.includes()` check ever runs). These are
// queue_type strings the view has actually emitted, shown here only to
// illustrate the rule above: `serious_ae` (the baseline value), plus
// `serious_ae_review`, `ae_followup`, `safety_check`. Each one matches
// because it SPLITS into a token from the list above (`serious`/`ae`,
// `serious`+`ae`+`review`, `ae`+`followup`, `safety`+`check`) — not because
// the full string is itself a recognised token.
export const QUEUE_SAFETY_TOKENS = Object.freeze(['ae', 'safety', 'serious']);

// ─── RPCs (SECURITY DEFINER functions — the only write paths for
//     multi-row invariants; see migrations for signatures) ─────────────────
export const RPCS = Object.freeze({
  PROMOTE_CONSENT_TO_CASE: 'promote_consent_to_case',
  APPEND_NOTE: 'rpc_append_note',
  ARCHIVE_CASE: 'rpc_archive_case',
  COMPLETE_TASK: 'rpc_complete_task',
  MARK_AE_NOTIFIED: 'rpc_mark_ae_notified',
  REPORT_ADVERSE_EVENT: 'rpc_report_adverse_event',
  SET_STAGE: 'rpc_set_stage',
  UPDATE_PRESCRIPTION: 'rpc_update_prescription',
  UPDATE_CONSENT_STATUS: 'update_consent_status',
  // Bulk import (Phase 3 Task B, F-IMPORT-*). Called byte-identically to v_20:
  // `.rpc(name, { p_rows: chunk })` returning {imported, total, errors[], duplicates[]}.
  // NEW patients → consents (New consents review); LEGACY patients → cases/triage.
  BULK_IMPORT_CONSENTS: 'rpc_bulk_import_consents',
  BULK_IMPORT_LEGACY: 'rpc_bulk_import_legacy',
  // Phase 6 (R-078). The helper created by migration
  // 20260719120000_enforce_is_active_in_rls.sql. The Admin view CALLS it purely
  // to discover whether that migration is applied on THIS database — a present,
  // executable function means every RLS policy carries the is_active check and
  // deactivation is server-authoritative; a missing function (PGRST202) means
  // deactivation is still client-only. We probe rather than assert because the
  // deployed DB and this repo can legitimately be out of step, and a hard-coded
  // "Row-level security enforced ✓" (what v_20 painted on) is a lie the founder
  // cannot detect.
  IS_ACTIVE_STAFF: 'is_active_staff',
  // Phase 6 (R-021). The MFA LOCKOUT RECOVERY path. supabase-js in a browser
  // cannot delete another user's MFA factor — auth.admin.* needs the
  // service_role key, which must never ship in a static bundle. So recovery
  // requires a SECURITY DEFINER function on the database that verifies the
  // caller is an admin and then deletes the factor. It may not be deployed on
  // the database a given build talks to, so data/mfa.js PROBES for it and
  // prints the manual dashboard runbook when it is absent — never a button
  // that silently does nothing at the exact moment someone is locked out.
  ADMIN_REMOVE_MFA_FACTOR: 'admin_remove_mfa_factor',
});

// ─── Storage buckets ───────────────────────────────────────────────────────
export const BUCKETS = Object.freeze({
  CONSENT_FORMS: 'consent-forms',
  EXPENSE_RECEIPTS: 'expense-receipts',
  TRAINING: 'training',
});

// ─── Pipeline stages (display vocabulary) ──────────────────────────────────
// The 8 display stages of the patient pipeline, in board order. key is the
// client-side display key; l/s are long/short labels; c the board colour.
export const STAGES = Object.freeze([
  Object.freeze({ key: 'triage', l: 'Triage', s: 'Triage', c: '#DC2626' }),
  Object.freeze({ key: 'enrolled', l: 'Enrolled', s: 'Enrolled', c: '#94A3B8' }),
  Object.freeze({ key: 'welcome', l: 'Welcome Call', s: 'Welcome', c: '#D97706' }),
  Object.freeze({ key: 'sha_pending', l: 'SHA Pending', s: 'SHA', c: '#C2410C' }),
  Object.freeze({ key: 'sha_approved', l: 'SHA Approved', s: 'Approved', c: '#65A30D' }),
  Object.freeze({ key: 'delivery', l: 'Delivery', s: 'Delivery', c: '#0891B2' }),
  Object.freeze({ key: 'active', l: 'On Therapy', s: 'Active', c: '#1F4477' }),
  Object.freeze({ key: 'refill', l: 'Refill Due', s: 'Refill', c: '#7C3AED' }),
]);

// Maps the reliable cases.current_stage ENUM to a STAGES index. This is the
// PREFERRED classifier; the free-text substring logic in resolveStage() is the
// fallback for any value not present here (legacy rows, closed_* states).
//
// CRITICAL INVARIANT (ported verbatim from legacy): this map is the EXACT
// INVERSE of STAGE_ENUM_BY_KEY below. For every Set-Stage option the round trip
// is IDENTITY: option index i -> set-stage writes enum E -> STAGE_ENUM_MAP[E]
// === i, so the pipeline pill never jumps after a manual Set Stage. The first
// 8 entries are the enums Set Stage writes; the "secondary" enums are
// case_stage values no Set-Stage option writes (extra adherence/review/SHA
// follow-up steps from the legacy import + scheduler), mapped to the most
// correct STAGES index by SEMANTICS. The closed_*/archived terminal states
// have NO STAGES index and are deliberately LEFT OUT — resolveStage falls
// through to the substring fallback for those.
export const STAGE_ENUM_MAP = Object.freeze({
  // ── Inverse of Set Stage (round-trip identity, indices 0..7) ──
  triage: 0,
  welcome_call_due: 1,
  sha_application_due: 2,
  sha_follow_up_due: 3,
  delivery_planning_due: 4,
  receipt_confirmation_due: 5,
  adherence_day15_due: 6,
  restart_follow_up_due: 7,
  // ── Secondary enums NOT written by Set Stage (semantic best-fit) ──
  treatment_start_confirmation_due: 5,
  receipt_confirmation_due_legacy: 5, // defensive alias; harmless if absent
  doctor_review_due: 6,
  adherence_day30_due: 6,
  review_preparation_due: 6,
});

// Display key -> case_stage enum written by Set Stage (legacy execSetStage).
// EXACT inverse of the first 8 STAGE_ENUM_MAP entries — see invariant above.
export const STAGE_ENUM_BY_KEY = Object.freeze({
  triage: 'triage',
  enrolled: 'welcome_call_due',
  welcome: 'sha_application_due',
  sha_pending: 'sha_follow_up_due',
  sha_approved: 'delivery_planning_due',
  delivery: 'receipt_confirmation_due',
  active: 'adherence_day15_due',
  refill: 'restart_follow_up_due',
});

// Terminal current_stage values (no STAGES index — never on the board).
export const CLOSED_STAGE_VALUES = Object.freeze([
  'closed',
  'closed_deceased',
  'closed_declined',
  'closed_stop',
  'closed_switch',
  'archived',
]);

/**
 * resolveStage(arg): arg may be a CASE OBJECT (preferred — lets us read the
 * current_stage enum) or a plain status STRING (legacy callers). When given an
 * object we try current_stage via STAGE_ENUM_MAP first, then fall back to the
 * substring logic on status. When given a string we use the substring logic
 * directly. Exact port of legacy resolveStage (default: 1 / Enrolled).
 * @param {any} arg case object or status string
 * @returns {number} STAGES index 0..7
 */
export function resolveStage(arg) {
  let status = arg;
  if (arg && typeof arg === 'object') {
    const ce = arg.current_stage;
    if (ce && Object.prototype.hasOwnProperty.call(STAGE_ENUM_MAP, ce)) return STAGE_ENUM_MAP[ce];
    status = arg.status; // fall back to the case's free-text status
  }
  const s = (status || '').toLowerCase();
  if (s.includes('refill') || s.includes('renew')) return 7;
  if (s.includes('active') || s.includes('therapy') || s.includes('adher')) return 6;
  if (s.includes('deliver') || s.includes('dispatch')) return 5;
  if (s.includes('approved')) return 4;
  if (s.includes('sha') || s.includes('reimburse')) return 3;
  if (s.includes('welcome') || s.includes('onboard')) return 2;
  if (s.includes('triage')) return 0;
  return 1;
}

/**
 * True when a case is closed/archived/deceased and so must NOT be bucketed
 * into the active pipeline STAGES (it would otherwise fall through
 * resolveStage's substring default to index 1 / "Enrolled" and inflate the
 * live board + counts). Exact legacy port.
 * @param {any} c case row
 * @returns {boolean}
 */
export function isClosedCase(c) {
  if (!c) return false;
  const ps = (c.program_status || '').toLowerCase();
  if (ps === 'closed' || ps === 'archived' || ps === 'deceased') return true;
  if ((c.current_stage || '').toLowerCase().startsWith('closed_')) return true;
  if (c.archived_at || c.is_archived) return true;
  return false;
}

// ─── SHA / SHIF reimbursement status (cases.sha_status) ────────────────────
// Values MUST match the DB CHECK constraint (migration 20260615120000):
//   not_started | submitted | approved | rejected | active
export const SHA_STATUS = Object.freeze({
  NOT_STARTED: 'not_started',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ACTIVE: 'active',
});

// Display metadata for SHA statuses (legacy SHA_STATUSES order preserved).
export const SHA_STATUSES = Object.freeze([
  Object.freeze({ v: SHA_STATUS.NOT_STARTED, l: 'Not started' }),
  Object.freeze({ v: SHA_STATUS.SUBMITTED, l: 'Submitted' }),
  Object.freeze({ v: SHA_STATUS.APPROVED, l: 'Approved' }),
  Object.freeze({ v: SHA_STATUS.REJECTED, l: 'Rejected' }),
  Object.freeze({ v: SHA_STATUS.ACTIVE, l: 'Active' }),
]);

// ─── Consent review statuses (update_consent_status p_status enum) ─────────
export const CONSENT_STATUS = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  DECLINED: 'declined',
  ON_HOLD: 'on_hold',
});

// ─── Adverse-event vocabulary ──────────────────────────────────────────────
// Open-set statuses used by the AE flag loader (`.in('status', …)`).
export const AE_OPEN_STATUSES = Object.freeze(['open', 'under_review', 'reported_to_pv']);
export const AE_SERIOUSNESS = Object.freeze({ SERIOUS: 'serious', NON_SERIOUS: 'non_serious' });

// Severity ladder (legacy ae-sev select, in order). Values match the RPC payload.
export const AE_SEVERITY = Object.freeze([
  Object.freeze({ v: 'mild', l: 'Mild' }),
  Object.freeze({ v: 'moderate', l: 'Moderate' }),
  Object.freeze({ v: 'severe', l: 'Severe' }),
  Object.freeze({ v: 'life_threatening', l: 'Life-threatening' }),
  Object.freeze({ v: 'fatal', l: 'Fatal' }),
]);

// Outcome at time of report (legacy ae-out select, in order).
export const AE_OUTCOME = Object.freeze([
  Object.freeze({ v: 'unknown', l: 'Unknown at time of report' }),
  Object.freeze({ v: 'recovering', l: 'Recovering' }),
  Object.freeze({ v: 'recovered', l: 'Recovered / resolved' }),
  Object.freeze({ v: 'not_recovered', l: 'Not recovered / ongoing' }),
  Object.freeze({ v: 'recovered_with_sequelae', l: 'Recovered with sequelae' }),
  Object.freeze({ v: 'fatal', l: 'Fatal' }),
]);

// ICH seriousness criteria (legacy ae-crit checkboxes, in order).
export const AE_SERIOUSNESS_CRITERIA = Object.freeze([
  Object.freeze({ v: 'death', l: 'Death' }),
  Object.freeze({ v: 'life_threatening', l: 'Life-threatening' }),
  Object.freeze({ v: 'hospitalization', l: 'Hospitalization / prolonged' }),
  Object.freeze({ v: 'disability', l: 'Persistent disability' }),
  Object.freeze({ v: 'congenital_anomaly', l: 'Congenital anomaly' }),
  Object.freeze({ v: 'other_medically_significant', l: 'Other medically significant' }),
]);

// AE notification config (legacy AE_EMAIL / AE_FORCE_RECIPIENT / SUSPECT_DEFAULT).
// The PV inbox is domain-locked to @insightsiq.co: this is an IDENTIFIABLE
// pharmacovigilance report authorised by the patient's signed consent, and it
// must NEVER be routed to an external address. AE_FORCE_RECIPIENT stays null.
export const AE_EMAIL = Object.freeze({
  to: 'ae-team@insightsiq.co',
  cc: 'safety@insightsiq.co',
  signature_block: 'Tumaini PSP · InsightsIQ\nThis report was generated automatically from the Tumaini admin app.',
});
export const AE_FORCE_RECIPIENT = null;
export const SUSPECT_DEFAULT = 'Abiraterone 250mg tablet';
// v_safety_reconciliation.reconciliation_status display vocabulary.
export const RECONCILIATION_STATUS = Object.freeze({
  RECONCILED: 'reconciled',
  AWAITING_ACK: 'awaiting_ack',
  // any other value renders as "Send email" (not yet sent) in the register
});

// ─── Archive / closure lifecycle (R-024 outcome capture at closure) ────────
// ARCHIVE_REASONS mirrors the archive_reason ENUM in the database and is ported
// BYTE-IDENTICALLY from legacy v20_Index.html (values + leads_to + severity).
// rpc_archive_case(p_case_id, p_reason, p_notes, p_archive_date) closes the
// case, cancels open tasks, writes the audit event and sets the closed_* stage
// implied by `leads_to`. The `value` is what goes on cases.archive_reason_code —
// it MUST NOT drift (v_20 and 2.0 write the same enum during the parallel run).
export const ARCHIVE_REASONS = Object.freeze([
  Object.freeze({ value: 'deceased', label: 'Patient deceased', leads_to: 'closed_deceased', severity: 'danger' }),
  Object.freeze({ value: 'treatment_completed', label: 'Treatment completed successfully', leads_to: 'closed_completed', severity: 'ok' }),
  Object.freeze({ value: 'side_effects_severe', label: 'Stopped: severe side effects', leads_to: 'closed_stop', severity: 'danger' }),
  Object.freeze({ value: 'doctor_advised_stop', label: 'Stopped: doctor advised', leads_to: 'closed_stop', severity: 'warn' }),
  Object.freeze({ value: 'treatment_failure', label: 'Stopped: treatment not working', leads_to: 'closed_stop', severity: 'warn' }),
  Object.freeze({ value: 'medication_switched_off_program', label: 'Switched to a different medication', leads_to: 'closed_switch', severity: 'warn' }),
  Object.freeze({ value: 'transferred_to_another_program', label: 'Transferred to another program', leads_to: 'closed_switch', severity: '' }),
  Object.freeze({ value: 'withdrew_consent', label: 'Patient withdrew consent', leads_to: 'closed_stop', severity: 'warn' }),
  Object.freeze({ value: 'patient_request_financial', label: 'Patient request: financial reasons', leads_to: 'closed_stop', severity: '' }),
  Object.freeze({ value: 'patient_request_personal', label: 'Patient request: personal reasons', leads_to: 'closed_stop', severity: '' }),
  Object.freeze({ value: 'moved_out_of_area', label: 'Moved out of catchment area', leads_to: 'closed_stop', severity: '' }),
  Object.freeze({ value: 'lost_to_follow_up', label: 'Lost to follow-up', leads_to: 'closed_stop', severity: 'warn' }),
  Object.freeze({ value: 'unreachable_extended', label: 'Unreachable >30 days', leads_to: 'closed_stop', severity: 'warn' }),
  Object.freeze({ value: 'non_adherence_severe', label: 'Severe non-adherence', leads_to: 'closed_stop', severity: 'warn' }),
  Object.freeze({ value: 'sha_denied_permanent', label: 'SHA denied permanently', leads_to: 'closed_declined', severity: 'danger' }),
  Object.freeze({ value: 'other', label: 'Other (specify in notes)', leads_to: 'closed_stop', severity: '' }),
]);

// Human label for a closed_* terminal stage (legacy updateArchiveHint map).
export const CLOSED_STAGE_LABELS = Object.freeze({
  closed_deceased: 'closed (deceased)',
  closed_completed: 'closed (treatment completed successfully)',
  closed_switch: 'closed (switched off program)',
  closed_declined: 'closed (declined)',
  closed_stop: 'closed (stopped)',
});

// R-024 clinical OUTCOME vocabulary captured at closure. There is no dedicated
// cases.outcome column yet (M5) — the outcome is interim-encoded alongside the
// archive_reason_code (see data/cases.js encodeArchiveNotes / archiveCase). This
// coarse clinical taxonomy sits ABOVE the finer archive_reason_code so a report
// can group closures by what happened to the patient, not just the admin reason.
export const CLOSURE_OUTCOMES = Object.freeze([
  Object.freeze({ v: 'completed', l: 'Treatment completed' }),
  Object.freeze({ v: 'died', l: 'Died' }),
  Object.freeze({ v: 'discontinued_adverse', l: 'Discontinued — adverse effects' }),
  Object.freeze({ v: 'discontinued_clinical', l: 'Discontinued — clinical decision' }),
  Object.freeze({ v: 'transferred', l: 'Transferred / switched programme' }),
  Object.freeze({ v: 'withdrew', l: 'Withdrew / patient choice' }),
  Object.freeze({ v: 'lost_to_follow_up', l: 'Lost to follow-up' }),
  Object.freeze({ v: 'not_reimbursed', l: 'Not reimbursed (SHA denied)' }),
  Object.freeze({ v: 'other', l: 'Other' }),
]);

// ─── Clinical profile vocabularies ─────────────────────────────────────────
// cases.cancer_stage and cases.delivery_method are plain text columns, so the
// only thing keeping them reportable is that everyone picks from the SAME list.
// These are the lists: the capture form renders them and the report bands on
// them. Free-typed variants would split one real cohort across several bars.

// Deliberately kept to the four Roman numerals plus the two honest non-answers.
// A staging system with sub-stages (IIa/IIb) belongs in clinical_notes — this
// column exists to make cohorts comparable, not to be a staging record.
export const CANCER_STAGES = Object.freeze([
  Object.freeze({ v: 'stage_i', l: 'Stage I' }),
  Object.freeze({ v: 'stage_ii', l: 'Stage II' }),
  Object.freeze({ v: 'stage_iii', l: 'Stage III' }),
  Object.freeze({ v: 'stage_iv', l: 'Stage IV' }),
  Object.freeze({ v: 'not_staged', l: 'Not staged' }),
  Object.freeze({ v: 'not_applicable', l: 'Not applicable (non-oncology)' }),
]);

// How medicine physically reaches the patient. 'psv' is public service vehicle —
// the normal upcountry route in Kenya and a genuinely different reliability
// profile from a courier, so it is its own option rather than "other".
export const DELIVERY_METHODS = Object.freeze([
  Object.freeze({ v: 'pickup', l: 'Patient collects at facility' }),
  Object.freeze({ v: 'courier', l: 'Courier to address' }),
  Object.freeze({ v: 'psv', l: 'PSV / bus parcel' }),
  Object.freeze({ v: 'caregiver', l: 'Caregiver collects' }),
  Object.freeze({ v: 'in_clinic', l: 'Administered in clinic' }),
]);

// ─── Task lifecycle (tasks.status) ─────────────────────────────────────────
export const TASK_STATUS = Object.freeze({
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  SNOOZED: 'snoozed',
  COMPLETED: 'completed',
});

// ─── Delivery lifecycle (deliveries.status) ────────────────────────────────
export const DELIVERY_STATUS = Object.freeze({
  PLANNED: 'planned',
  DISPATCHED: 'dispatched',
  IN_TRANSIT: 'in_transit',
  RECEIVED: 'received',
  TREATMENT_STARTED: 'treatment_started',
});

// ─── Programme membership (cases.program_status) ───────────────────────────
export const PROGRAM_STATUS = Object.freeze({
  ACTIVE: 'active',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
  DECEASED: 'deceased',
});

// ─── Paging ────────────────────────────────────────────────────────────────
// PostgREST hard-caps a single request at 1000 rows; fetchAll() loops .range()
// in chunks of this size (legacy PT_CHUNK).
export const PT_CHUNK = 1000;
